import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { AppDispatch, RootState } from 'redux/store';

import client, { authClient } from 'api/apiClient';
import Login from 'pages/Auth/Login';
import Layout from 'components/Common/Layout';
import ForgotPassword from 'pages/Auth/ForgotPassword';
import VerifyTempPassword from 'pages/Auth/VerifyTempPassword';
import SendInvoice from 'pages/Invoice/SendInvoice';
import Staff from 'pages/Staff/Staff';
import CreateNewStaff from 'pages/Staff/CreateNewStaff';
import MyAccount from 'pages/Account/MyAccount';
import MyAccountChangePassword from 'pages/Account/MyAccountChangePassword';
import AccountChangePhoto from 'pages/Account/AccountChangePhoto';
import routes from 'constants/routes';
import DashboardScreen from 'pages/Dashboard/DashboardScreen';
import ProtectedRoute from 'auth/ProtectedRoute';
import NotFound from 'pages/NotFound/NotFound';
import ErrorBoundary from 'pages/ErrorBoundary';

import './App.css';
import VerifyPinCode from 'pages/Auth/VerifyPinCode';
import SendAndRequest from 'pages/SendAndRequest/SendAndRequest';
import UserDetail from 'pages/User/UserDetail';
import BusinessChangePhoto from 'pages/Account/BusinessChangePhoto';
import RequestMoneyTransactionDetail from 'components/SendAndRequest/RequestMoneyTransactionDetail';
import { getMoneyContainer } from 'redux/slices/getMoneyContainerSlice';
import SearchBusinessAndUser from 'pages/SendAndRequest/SearchBusinessAndUser';
import { SEND_MONEY, SEND_REQUEST } from 'types/sendAndRequest';
import SendAndRequestMoneyForm from 'pages/SendAndRequest/SendAndRequestMoneyForm';
import RequireMerchant from 'auth/RequireMerchant';
import { getAuthData } from 'utils/helpers';
import SendMoneyTransactionDetail from 'pages/SendAndRequest/SendMoneyTransactionDetail';
import ExportCSVTable from 'components/CSV/ExportCSVTable';
import QRCodeGenerator from 'pages/QR/QRCodeGenerator';
import PaymentTokenDetail from 'pages/Account/PaymentTokenDetail';

function App() {
    const dispatch = useDispatch<AppDispatch>();
    const authData = getAuthData();
    const { refresh_token: refreshToken, access_token: accessToken, need_change_password } = authData || {};
    const companyId = useSelector((state: RootState) => state.user.defaultCompanyId);


    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        client && client.setDispatch(dispatch);
    }, [dispatch]);

    useEffect(() => {
        if (accessToken) {
            client.setToken(accessToken, refreshToken);
            authClient.setToken(accessToken, refreshToken);
        }
    }, [accessToken, dispatch, refreshToken]);

    useEffect(() => {
        if (companyId) {
            dispatch(getMoneyContainer());
        }
    }, [accessToken, dispatch, companyId, refreshToken]);

    return (
        <Router>
            <ErrorBoundary dispatch={dispatch}>
                <Routes>
                    {/* Login Route */}
                    <Route path={routes.auth.login} element={<Login />} />
                    {/* Forgot Password Route */}
                    <Route path={routes.auth.forgotPassword} element={<ForgotPassword />} />
                    {/* Verify pin Route */}
                    <Route path={routes.auth.verifyPin} element={<VerifyPinCode />}></Route>
                    {/* Verify Password Route */}
                    <Route element={<ProtectedRoute hideHeader />}>
                        {/* Change Password Route */}
                        <Route path={routes.auth.changePassword} element={<MyAccountChangePassword />} />
                    </Route>

                    {/* Verify Temp Password Route */}
                    <Route path={routes.auth.verifyTempPassword} element={<VerifyTempPassword />} />

                    {/* Routes With Layout */}
                    <Route element={<Layout />}>
                        {/* Protected Routes */}
                        <Route element={<ProtectedRoute />}>

                            {/* Send Invoice Route */}
                            <Route path={routes.sendinvoice.main} element={<SendInvoice />} />

                            {/* Staff Route */}
                            <Route element={<RequireMerchant />}>
                                <Route path={routes.staff.main} element={<Staff />} />
                                {/* Staff Add New Route */}
                                <Route path={routes.staff.add} element={<CreateNewStaff />} />
                            </Route>
                            {/* User */}
                            <Route path={routes.user.userDetail} element={<UserDetail />} />

                            {/* My Account Route */}
                            <Route path={routes.account.main} element={<MyAccount />} />
                            {/* My Account Route */}
                            <Route path={routes.account.changePhoto} element={<AccountChangePhoto />} />
                            {/* Business profile change photo Route */}
                            <Route path={routes.account.businessChangePhoto} element={<BusinessChangePhoto />} />
                            <Route path={routes.account.paymentTokenDetail} element={<PaymentTokenDetail />} />
                            {/* My Account Change Password */}
                            <Route path={routes.account.changePassword} element={<MyAccountChangePassword type="resetPassword" />} />

                            {/* Send and Request Route */}
                            <Route path={routes.sendAndRequest.main} element={<SendAndRequest />} />
                            {/* SendMoney Details Route */}

                            <Route path={routes.sendAndRequest.sendMoneyForm} element={<SendAndRequestMoneyForm title={SEND_MONEY} />} />

                            <Route
                                path={routes.sendAndRequest.requestMoneyForm}
                                element={<SendAndRequestMoneyForm title={SEND_REQUEST} />}
                            />

                            <Route path={routes.sendAndRequest.requestMoney} element={<SearchBusinessAndUser title={SEND_REQUEST} />} />

                            <Route path={routes.sendAndRequest.sendMoney} element={<SearchBusinessAndUser title={SEND_MONEY} />} />

                            <Route
                                path={routes.sendAndRequest.requestMoneyTransactionDetails}
                                element={<RequestMoneyTransactionDetail />}
                            />

                            {/* Wallet Route */}
                            <Route path={routes.dashboard.main} element={<DashboardScreen />} />
                            <Route path={routes.transactions.transactionDetails} element={<SendMoneyTransactionDetail />} />
                            <Route path={routes.export_CSV_file.exportCSV} element={<ExportCSVTable />}></Route>

                            {/* QR Routes */}
                            <Route path={routes.qr.main} element={<QRCodeGenerator />} />
                        </Route>
                    </Route>
                    <Route path="*" element={<NotFound />} />
                </Routes>
            </ErrorBoundary>
        </Router>
    );
}

export default App;
